import React from 'react';

import { MetricValuePill, SparkLine } from '@tableau/metrics-shared-widgets-dom';

import { getMetricMeasure, getTableWithDefaultComparison } from '..';
import { cssClass } from '../../style';
import { buildColorAccessor, buildFirstThresholdMeasureAccessor, buildMeasureAccessor, buildTimestampAccessor, computeMetricComparison, DataPointsApiResponse, DataRow, getLatestValue, getMeasureFormatString, getStylePropsForDefaultThresholdLine, MetricBlue, MetricsNeutralColor, shouldRenderFirstThresholdLine, ValueAccessor } from '@tableau/metrics-engine';
import { FeatureFlags } from '../../ts/FeatureFlags';

export type MetricSparklineAndPillProps = {
    readonly metricData: DataPointsApiResponse;
};

export class MetricSparklineAndPill extends React.PureComponent<MetricSparklineAndPillProps> {
    public render(): JSX.Element {
        const definition = this.props.metricData.definition;
        const timestampAccessor = buildTimestampAccessor(definition);
        const measureAccessor = buildMeasureAccessor(definition);
        const colorAccessor = buildColorAccessor(definition);
        const comparisonAccessor = buildFirstThresholdMeasureAccessor(definition);

        let measure;
        let format;

        if (this.props.metricData && this.props.metricData.data && this.props.metricData.data.length > 0) {
          measure = getMetricMeasure(this.props.metricData, measureAccessor);
          format = getMeasureFormatString(definition);
        }

        const showSparkline = this.props.metricData && this.props.metricData.data && this.props.metricData.data.length > 1 && timestampAccessor;

        const comparisonTable = this.props.metricData && getTableWithDefaultComparison(this.props.metricData);

        const latestValue = comparisonTable && getLatestValue(comparisonTable);
        const comparison = latestValue && computeMetricComparison(latestValue);

        const colorsEnabledColor =
          latestValue && comparison
            ? colorAccessor(comparison.comparedPoint)
            : MetricsNeutralColor;

        const color = FeatureFlags.metricColorIndicatorsEnabled ? colorsEnabledColor : MetricBlue;
        const comparisonLineStyle = getStylePropsForDefaultThresholdLine(definition);

        const shouldRenderReferenceLine = FeatureFlags.metricsReferenceLineEnabled && shouldRenderFirstThresholdLine(definition);
        return (
        <div data-tb-test-id="omnibox-metric-thumbnail">
            <span className={MetricOmniPillStyle} data-tb-test-id="omnibox-metric-pill">
              <MetricValuePill value={measure} measureFormat={format} color={color} />
            </span>
            {
              showSparkline &&
              <SparkLine
                size={MetricOmniSparklineSize}
                data={comparisonTable.slice(-30)}  // 100 points is still kind of slow so use only 30
                timestampAccessor={timestampAccessor}
                measureAccessor={measureAccessor}
                comparisonAccessor={FeatureFlags.metricsReferenceLineEnabled ? comparisonAccessor : undefined}
                style={MetricOmniSparklineStyle}
                color={color}
                testId={'omnibox-metric-sparkline'}
                shouldRenderReferenceLine={shouldRenderReferenceLine}
                formatString={format}
                comparisonLineStyle={comparisonLineStyle}
              />
            }
            {
              !showSparkline && <div style={{...MetricOmniSparklineStyle, ...MetricOmniSparklineSize}} />
            }
        </div>);
    }
}

const thumbnailWidth = 76;
const sparklineHeight = 18;

const MetricOmniPillStyle = cssClass('omni', 'metric-pill', {
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'row',
  paddingTop: 8,
  alignItems: 'center',
  justifyContent: 'center',
  width: thumbnailWidth + 'px',
  height: '20px',
});

const MetricOmniSparklineStyle = {paddingTop: '8px', paddingBottom: '8px'};
const MetricOmniSparklineSize = { width: thumbnailWidth, height: sparklineHeight};
